import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/firestore";
import "firebase/compat/auth";
import "firebase/compat/storage";
import "firebase/compat/functions";
import { getApp } from "firebase/app";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// Consolidate config into a single object for clarity.
// Omit measurementId so the SDK uses the value from Firebase (same as unified-app). Setting
// REACT_APP_FIREBASE_MEASUREMENT_ID here when the server returns undefined triggers Analytics
// warnings: "The measurement ID in the local Firebase config does not match...".
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// initializeTestApp exists only with @firebase/rules-unit-testing / legacy testing helpers, not on compat typings.
function initFirebaseApp() {
  const fb =
    /** @type {typeof firebase & { initializeTestApp?: (cfg: object) => ReturnType<typeof firebase.initializeApp> }} */ (
      firebase
    );
  const useTestApp =
    process.env.NODE_ENV === "test" && typeof fb.initializeTestApp === "function";
  if (useTestApp) {
    return fb.initializeTestApp({
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      auth: { uid: "test-user", email: "test@example.com" },
    });
  }
  return firebase.initializeApp(firebaseConfig);
}

const firebaseApp =
  Array.isArray(firebase.apps) && firebase.apps.length > 0 ? firebase.app() : initFirebaseApp();

/** Match deployed 1st-gen Functions region (see firebase deploy). */
const FUNCTIONS_REGION = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || "us-central1";
const FUNCTIONS_BACKEND_MODE_ENV = String(process.env.REACT_APP_FUNCTIONS_BACKEND_MODE || "")
  .trim()
  .toLowerCase();
const FUNCTIONS_BACKEND_MODE = ["auto", "hosted", "emulator"].includes(FUNCTIONS_BACKEND_MODE_ENV)
  ? FUNCTIONS_BACKEND_MODE_ENV
  : "auto";

/** Must match emulator bind host (firebase.json / CLI default is 127.0.0.1). */
const FUNCTIONS_EMULATOR_HOST = "127.0.0.1";
const FUNCTIONS_EMULATOR_PORT = 5001;

function isLocalWebHostname(hostname) {
  if (!hostname) return false;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1"
  );
}

function shouldUseFunctionsEmulator() {
  if (FUNCTIONS_BACKEND_MODE === "hosted") return false;
  return (
    process.env.NODE_ENV !== "test" &&
    typeof window !== "undefined" &&
    (FUNCTIONS_BACKEND_MODE === "emulator" || process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === "true") &&
    isLocalWebHostname(window.location?.hostname)
  );
}

function connectLocalFunctionsEmulator() {
  if (!shouldUseFunctionsEmulator()) return;
  try {
    connectFunctionsEmulator(
      getFunctions(getApp(), FUNCTIONS_REGION),
      FUNCTIONS_EMULATOR_HOST,
      FUNCTIONS_EMULATOR_PORT
    );
  } catch (e) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[healthdesk/shared-services] connectFunctionsEmulator:", e?.message || e);
    }
  }
}

// Only initialize analytics in production
if (process.env.NODE_ENV === "production") {
  firebase.analytics();
}

const db = firebaseApp.firestore();
const storage = firebaseApp.storage();

// Wire emulator before any httpsCallable (AuthProvider imports this module early).
connectLocalFunctionsEmulator();
const functions = firebaseApp.functions(FUNCTIONS_REGION);
if (shouldUseFunctionsEmulator()) {
  try {
    functions.useEmulator(FUNCTIONS_EMULATOR_HOST, FUNCTIONS_EMULATOR_PORT);
  } catch (e) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[healthdesk/shared-services] compat functions.useEmulator:", e?.message || e);
    }
  }
}
if (typeof window !== "undefined" && process.env.NODE_ENV !== "test" && typeof console !== "undefined" && console.info) {
  const effectiveMode = shouldUseFunctionsEmulator() ? "emulator" : "hosted";
  console.info(
    `[healthdesk/shared-services] Functions backend: ${effectiveMode} (project=${firebaseConfig.projectId || "unknown"}, region=${FUNCTIONS_REGION}, mode=${FUNCTIONS_BACKEND_MODE})`
  );
}

export default firebaseApp;
export { db, storage, functions, FUNCTIONS_REGION };
